import { useState } from 'react';
import axios from 'axios';
import { User, Lock, Sun, Moon, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../zustand/UserStore';
import { getApiBaseUrl } from '../utils/config';
import { getNavigationPermissions } from '../hooks/useAuthorization';
import { useTheme } from '../zustand/ThemeStore';
import logo from '../assets/Final Logo Used in White Label.png';

export default function SignIn() {
  const isDarkMode = useTheme((set)=>set.theme);
  const setIsDarkMode = useTheme((set)=> set.setTheme);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const setUser = useUserStore((set)=>set.setUser)
  const nav = useNavigate();

  const handleLogin = async () => {
    if (email && password) {
      setIsLoading(true);
      try {
        const response = await axios.post(`${getApiBaseUrl()}/api/v1/auth/signin`, { email, password });
        const { idToken, user_data } = response.data;
        if (idToken) {
          setUser(user_data);
          localStorage.setItem('token', idToken);
          
          // Navigate based on user role
          const navPermissions = getNavigationPermissions(user_data.role, user_data.uid);
          nav(navPermissions.defaultRoute);
        } else {
          toast.error('Login failed: No token received.');
        }
      } 
      catch (error: any) {
        console.error('Login failed: ' + (error.response?.data?.message || error.message));
        
        // Check if user account is suspended
        if (error.response?.status === 401 && error.response?.data?.detail == "USER_DISABLED") {
          toast.error('Your account has been suspended. These credentials are not active. Please contact your administrator.');
        } else {
          toast.error('Login Failed');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleForgotPassword = async () => {
    if (!email || email.trim() === '') {
      toast.error('Please enter your email address first');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSendingReset(true);
    try {
      await axios.post(`${getApiBaseUrl()}/api/v1/auth/forgot-password`, { email });
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (error: any) {
      console.error('Forgot password error:', error);
      // Always show success message for security (don't reveal if email exists)
      toast.success('If an account exists with this email, a password reset link has been sent.');
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="absolute top-0 left-0 right-0 p-6 px-20 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className={`w-32 text-center rounded-lg ${isDarkMode ? "bg-white": ""} flex items-center justify-center h-16`}>
            <img src={logo} alt="Stride Logo" className="w-28 h-28 object-contain" />
          </div>
        </div>

        <button
          onClick={toggleTheme}
          className={`p-2.5 rounded-lg transition-all duration-200 ${
            isDarkMode
              ? 'bg-[#0a1414] hover:bg-[#0f1919] text-yellow-400 border border-[#1a2424]'
              : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
          }`}
          aria-label="Toggle theme"
        >
          {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex items-center justify-center min-h-screen px-4">
        <div className={`w-full max-w-md rounded-2xl shadow-2xl p-8 transition-all duration-300 ${
          isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
        }`}>

          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold mb-2 ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Welcome Back
            </h2>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-[#373737]'
            }`}>
              Sign in to continue to Stride Call Analyzer
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className={`h-5 w-5 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full pl-10 pr-3 py-2.5 rounded-lg transition-all duration-200 outline-none ${
                    isDarkMode
                      ? 'bg-[#050a0a] text-white placeholder-gray-500 focus:bg-[#0f1919] focus:ring-2 focus:ring-[#1a9f6b] border border-[#1a2424]'
                      : 'bg-gray-100 text-[#053333] placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleLogin()}
                  className={`w-full pl-10 pr-10 py-2.5 rounded-lg transition-all duration-200 outline-none ${
                    isDarkMode
                      ? 'bg-[#050a0a] text-white placeholder-gray-500 focus:bg-[#0f1919] focus:ring-2 focus:ring-[#1a9f6b] border border-[#1a2424]'
                      : 'bg-gray-100 text-[#053333] placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className={`h-5 w-5 ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                    }`} />
                  ) : (
                    <Eye className={`h-5 w-5 ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                    }`} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button 
                type="button"
                onClick={handleForgotPassword}
                disabled={isSendingReset || !email}
                className={`text-sm transition-colors ${
                  !email 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-[#1a9f6b] hover:text-[#148856] cursor-pointer'
                }`}
              >
                {isSendingReset ? 'Sending...' : 'Forgot password?'}
              </button>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading || !email || !password}
              className="w-full bg-[#1a9f6b] hover:bg-[#148856] text-white py-2.5 rounded-lg font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute -top-1/2 -right-1/2 w-full h-full rounded-full blur-3xl ${
          isDarkMode ? 'bg-[#1a9f6b]/5' : 'bg-[#1a9f6b]/3'
        }`}></div>
        <div className={`absolute -bottom-1/2 -left-1/2 w-full h-full rounded-full blur-3xl ${
          isDarkMode ? 'bg-[#1a9f6b]/5' : 'bg-[#1a9f6b]/3'
        }`}></div>
      </div>
    </div>
  );
}